import {
	DOM_FPS_TAGNAME,
	IFloodgateStringProvider,
	IFpsSurvey,
	ISurveyComponent,
	ISurveyInfo,
	IUxSchema,
	JSON_APPLICATION_KEYNAME,
	JSON_COMMENT_KEYNAME,
	JSON_EXTENDEDMANIFESTDATA_KEYNAME,
	JSON_MULTIPLECHOICE_KEYNAME,
	JSON_PROMPT_KEYNAME,
	JSON_RATING_KEYNAME,
	JSON_SURVEYSPECIFICDATA_KEYNAME,
	JSON_SURVEYSTRINGS_KEYNAME,
	SurveyComponentType,
	SurveyType,
} from "@ms-ofb/floodgate-shared-typings";
import { CampaignSurveyContent, CampaignSurveyTemplate } from "../Campaign/CampaignDefinitionProvider";
import CommentComponent from "../SurveyComponents/CommentComponent";
import CVSurveyComponent from "../SurveyComponents/CVSurveyComponent";
import MultipleChoiceComponent from "../SurveyComponents/MultipleChoiceComponent";
import PromptComponent from "../SurveyComponents/PromptComponent";
import RatingComponent from "../SurveyComponents/RatingComponent";
import * as Utils from "../Utils";
import { Survey } from "./Survey";
import SurveyDataSource from "./SurveyDataSource";

const { isNOU } = Utils;

class FpsSurvey extends Survey implements IFpsSurvey {
	public static make(data: FpsSurvey.FpsSurveyData): IFpsSurvey {
		try {
			return new FpsSurvey(data);
		} catch (e) {
			return null;
		}
	}

	public static makeFps(baseData: SurveyDataSource.SurveyDataSourceData, sp: IFloodgateStringProvider,
		surveyModel: CampaignSurveyTemplate): IFpsSurvey {

		if (isNOU(baseData) || isNOU(sp) || isNOU(surveyModel)) {
			return null;
		}

		const ratingValuesAscending: string[] = [];
		const availableOptions: string[] = [];
		const content: CampaignSurveyContent = surveyModel.content;
		let contentValid = true;

		// Either UxSchema or Prompt/Content must be in the survey
		// In case of Prompt/Content, Prompt is required and at least one of the other components are required
		if (isNOU(content) || isNOU(content.prompt) ||
			(isNOU(content.rating) && isNOU(content.multipleChoice) &&
				isNOU(content.comment))) {
			contentValid = false;
			if (!surveyModel.uxSchema) {
				return null;
			}
		}

		const data = new FpsSurvey.FpsSurveyData();
		data.baseData = baseData;
		data.uxSchema = surveyModel.uxSchema;

		if (contentValid) {
			data.promptData = new PromptComponent.PromptComponentData();
			data.promptData.title = sp.getCustomString(content.prompt.title);
			data.promptData.question = sp.getCustomString(content.prompt.question);
			data.promptData.yesButtonLabel = sp.getCustomString(content.prompt.yesLabel);
			data.promptData.noButtonLabel = sp.getCustomString(content.prompt.noLabel);

			if (isNOU(data.promptData.title)
				|| isNOU(data.promptData.question)
				|| isNOU(data.promptData.yesButtonLabel)
				|| isNOU(data.promptData.noButtonLabel)) {

				return null;
			}

			if (content.rating) {
				data.ratingData = new RatingComponent.RatingComponentData();
				data.ratingData.question = sp.getCustomString(content.rating.question);
				data.ratingData.isZeroBased = content.rating.isZeroBased;

				for (const value of content.rating.ratingValuesAscending) {
					const customString: string = sp.getCustomString(value);

					if (isNOU(customString)) {
						return null;
					}

					ratingValuesAscending.push(customString);
				}

				data.ratingData.ratingValuesAscending = ratingValuesAscending;

				if (isNOU(data.ratingData.question)
					|| isNOU(data.ratingData.ratingValuesAscending)) {

					return null;
				}
			}

			if (content.multipleChoice) {
				data.multipleChoiceData = new MultipleChoiceComponent.MultipleChoiceComponentData();
				data.multipleChoiceData.question = sp.getCustomString(content.multipleChoice.question);
				data.multipleChoiceData.minNumberOfSelectedOptions = content.multipleChoice.minNumberOfSelectedOptions;
				data.multipleChoiceData.maxNumberOfSelectedOptions = content.multipleChoice.maxNumberOfSelectedOptions;

				for (const value of content.multipleChoice.availableOptions) {
					const customString: string = sp.getCustomString(value);

					if (isNOU(customString)) {
						return null;
					}

					availableOptions.push(customString);
				}

				data.multipleChoiceData.availableOptions = availableOptions;

				if (isNOU(data.multipleChoiceData.question)
					|| isNOU(data.multipleChoiceData.availableOptions)) {

					return null;
				}
			}

			if (content.comment) {
				data.commentData = new CommentComponent.CommentComponentData();
				data.commentData.question = sp.getCustomString(content.comment.question);

				if (isNOU(data.commentData.question)) {
					return null;
				}
			}

			// cvsurvey data is not required on all surveys.
			if (content.cvSurvey) {
				data.cvSurveyData = new CVSurveyComponent.CVSurveyComponentData();
				data.cvSurveyData.customerVoiceFormId = content.cvSurvey.customerVoiceFormId;

				if (isNOU(data.cvSurveyData.customerVoiceFormId)) {
					return null;
				}
			}
		}

		return this.make(data);
	}

	private surveyInfo: SurveyDataSource;
	private question: CommentComponent;
	private prompt: PromptComponent;
	private rating: RatingComponent;
	private multipleChoice: MultipleChoiceComponent;
	private cvSurvey: CVSurveyComponent;
	private contentValid: boolean;

	private constructor(data: FpsSurvey.FpsSurveyData) {
		super();
		if (isNOU(data)) {
			throw new Error("data must not be null");
		}

		this.surveyInfo = new SurveyDataSource(data.baseData);
		this.contentValid = (!Utils.isNOU(data.promptData?.question) && !Utils.isNOU(data.promptData?.title)) &&
		!Utils.isNOU(data.ratingData?.question) && !Utils.isNOU(data.ratingData?.ratingValuesAscending) &&
		!Utils.isNOU(data.commentData?.question);

		// Taking in the UxSchema if possible
		if (data.uxSchema) {
			this.setUxSchema(data.uxSchema);
		}

		// Otherwise, fallback to static content
		if (this.contentValid) {
			this.prompt = new PromptComponent(data.promptData);
			this.question = data.commentData ? (new CommentComponent(data.commentData)) : null;
			this.rating = data.ratingData ? (new RatingComponent(data.ratingData)) : null;
			this.multipleChoice = data.multipleChoiceData ? (new MultipleChoiceComponent(data.multipleChoiceData)) : null;
			this.cvSurvey = data.cvSurveyData ? (new CVSurveyComponent(data.cvSurveyData)) : null;
		}
	}

	// @Override
	public getType(): SurveyType {
		return SurveyType.Fps;
	}

	// @Override
	public getSurveyInfo(): ISurveyInfo {
		return this.surveyInfo;
	}

	// @Override
	public getCommentComponent(): CommentComponent {
		return this.question;
	}

	// @Override
	public getPromptComponent(): PromptComponent {
		return this.prompt;
	}

	// @Override
	public getRatingComponent(): RatingComponent {
		return this.rating;
	}

	// @Override
	public getMultipleChoiceComponent(): MultipleChoiceComponent {
		return this.multipleChoice;
	}

	public getCVSurveyComponent(): CVSurveyComponent {
		return this.cvSurvey;
	}

	// @Override
	public getTags(): string[] {
		return this.surveyInfo.getTags();
	}

	// @Override
	public getComponent(componentType: SurveyComponentType): ISurveyComponent {
		switch (componentType) {
			case SurveyComponentType.Comment:
				return this.getCommentComponent();
			case SurveyComponentType.Prompt:
				return this.getPromptComponent();
			case SurveyComponentType.Rating:
				return this.getRatingComponent();
			case SurveyComponentType.MultipleChoice:
				return this.getMultipleChoiceComponent();
			case SurveyComponentType.CVSurvey:
				return this.getCVSurveyComponent();
			default:
				return null;
		}
	}

	// @Override
	public getDomElements(doc: Document): Element[] {
		if (isNOU(doc)) {
			throw new Error("Document must not be null");
		}

		const element: Element = doc.createElement(DOM_FPS_TAGNAME);

		this.getSurveyInfo().getDomElements(doc).forEach((child) => {
			if (!isNOU(child)) {
				element.appendChild(child);
			}
		});

		this.getCommentComponent().getDomElements(doc).forEach((child) => {
			if (!isNOU(child)) {
				element.appendChild(child);
			}
		});

		if (this.getRatingComponent()) {
			this.getRatingComponent().getDomElements(doc).forEach((child) => {
				if (!isNOU(child)) {
					element.appendChild(child);
				}
			});
		}

		if (this.getMultipleChoiceComponent()) {
			this.getMultipleChoiceComponent().getDomElements(doc).forEach((child) => {
				if (!isNOU(child)) {
					element.appendChild(child);
				}
			});
		}

		return [element];
	}

	// @Override
	public getJsonElements(): object {
		let result: object = {};

		result = Utils.overrideValues(this.getSurveyInfo().getJsonElements(), result);
		result[JSON_APPLICATION_KEYNAME] = {};

		result[JSON_APPLICATION_KEYNAME][JSON_EXTENDEDMANIFESTDATA_KEYNAME] =
			JSON.stringify({
				[JSON_SURVEYSTRINGS_KEYNAME]: this.makeSurveyStrings(),
				[JSON_SURVEYSPECIFICDATA_KEYNAME]: this.makeResponseString(),
			});

		return result;
	}
	
	// @Override
	public isContentValid(): boolean {
		return this.contentValid;
	}

	// Makes the string to send to Manifest respecting the components present in the survey
	private makeResponseString(): string {
		const response: object = {};

		if (this.question) {
			response[JSON_COMMENT_KEYNAME] = [this.getCommentComponent().getSubmittedText()];
		}

		if (this.multipleChoice) {
			response[JSON_MULTIPLECHOICE_KEYNAME] =
				[this.getMultipleChoiceComponent().getOptionSelectedStates()];
		}

		if (this.rating) {
			response[JSON_RATING_KEYNAME] = [
				{
					numberOfOptions: this.getRatingComponent().getRatingValuesAscending().length,
					selectedRating: this.getRatingComponent().getSelectedRatingIndex(),
				}];
		}

		return JSON.stringify(response);
	}

	// Makes the string to send to Manifest respecting the components present in the survey
	private makeSurveyStrings(): string {
		const actualStrings: object = {};

		if (this.prompt) {
			actualStrings[JSON_PROMPT_KEYNAME] = this.prompt.getComponentJson();
		}

		if (this.rating) {
			actualStrings[JSON_RATING_KEYNAME] = [this.rating.getComponentJson()];
		}

		if (this.question) {
			actualStrings[JSON_COMMENT_KEYNAME] = [this.question.getComponentJson()];
		}

		if (this.multipleChoice) {
			actualStrings[JSON_MULTIPLECHOICE_KEYNAME] = [this.multipleChoice.getComponentJson()];
		}

		return JSON.stringify(actualStrings);
	}
}
module FpsSurvey {
	/**
	 * Data required for a Fps Survey
	 */
	export class FpsSurveyData {
		public baseData: SurveyDataSource.SurveyDataSourceData;
		public commentData: CommentComponent.CommentComponentData;
		public promptData: PromptComponent.PromptComponentData;
		public ratingData: RatingComponent.RatingComponentData;
		public multipleChoiceData: MultipleChoiceComponent.MultipleChoiceComponentData;
		public cvSurveyData: CVSurveyComponent.CVSurveyComponentData;
		public uxSchema: IUxSchema;
	}
}

export default FpsSurvey;
